import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { SourceDomain } from '@pflegia/types/api/source-domain';
import { stripFlavor } from '@pflegia/util/common/path-utils';
import { replaceDomain } from '@pflegia/util/common/replace-domain';

import { useFlavorFromRoute } from './use-flavor-from-route';
import { useStrippedRouter } from './use-stripped-router';

interface CanonicalUrlParam {
  defaultDomain: string;
  flavorDomain: string;
  path: string;
  url: string;
  removeQueryParams: boolean;
}

/**
 * Hook that returns a memoized function `getCanonicalUrl` that replaces the `defaultDomain` in `url` with `flavorDomain`.
 *
 * @returns A function that takes an object with `defaultDomain`, `flavorDomain`, `path`, and `url` properties
 * and returns a string where it replaces the `defaultDomain` in `url` (defaults to `process.env.NEXT_PUBLIC_BASE_URL`)
 *  with `flavorDomain` and appends the `path` which defaults to `router.asPath`.
 */
export const useCanonicalUrl = () => {
  const router = useRouter();
  const { isBlogPage, isJobDetailsPage, isExternalJobPage, isJobPage } = useStrippedRouter();
  const { isPraxia } = useFlavorFromRoute();

  const getCanonicalUrl = useCallback(
    ({
      flavorDomain,
      defaultDomain = SourceDomain.PflegiaDe,
      path = router.asPath,
      url = process.env.NEXT_PUBLIC_BASE_URL ?? 'http://localhost',
      removeQueryParams = false,
    }: Partial<CanonicalUrlParam>) =>
      replaceDomain(
        defaultDomain,
        flavorDomain,
        `${url}${stripFlavor(path)}`,
        isPraxia,
        removeQueryParams,
        true,
        isJobDetailsPage || isBlogPage || isExternalJobPage || isJobPage,
      ),
    [router.asPath, isPraxia, isJobDetailsPage, isBlogPage, isExternalJobPage, isJobPage],
  );

  return getCanonicalUrl;
};
